<template>
  <div
    class="border-2 border-black shadow-[4px_4px_0_0_rgba(0,0,0,1)] rounded-lg p-6 flex flex-col h-full min-w-0 max-w-full"
  >
    <div class="flex items-start justify-between mb-3 gap-3">
      <h3 class="text-lg font-semibold text-gray-900 truncate flex-1 min-w-0">
        {{ material.title }}
      </h3>
      <span
        :class="[
          'px-3 py-1 text-sm font-medium rounded-full flex-shrink-0',
          getTypeClasses(material.type),
        ]"
      >
        {{ material.type }}
      </span>
    </div>
    <div class="flex items-center justify-between">
      <div class="flex items-center text-sm text-gray-500 mb-2 min-w-0 flex-1 mr-2">
        <DocumentTextIcon class="w-4 h-4 mr-2 flex-shrink-0" />
        <span class="truncate" :title="material.subject">{{ material.subject }}</span>
      </div>
      <div class="flex items-center text-sm text-gray-500 mb-3 flex-shrink-0">
        <CalendarIcon class="w-4 h-4 mr-2" />
        {{ formattedDate }}
      </div>
    </div>
    <div
      class="text-sm border border-gray-300 rounded-md p-3 bg-gray-100 text-gray-600 mb-2 break-words overflow-hidden"
    >
      <span class="font-bold">Source: </span>
      <template v-if="material.filename && material.student_notes_url">
        <span
          @click="downloadStudentNotes"
          class="relative inline-flex items-center text-blue-600 hover:text-blue-800 cursor-pointer font-semibold underline decoration-2 decoration-blue-400 hover:decoration-blue-600 underline-offset-2 transition-all duration-200 hover:scale-105"
          title="Click to download original notes"
        >
          <ArrowDownCircleIcon class="w-4 h-4 mr-1 animate-pulse" />
          {{ material.filename }}
        </span>
        - {{ material.source }}
      </template>
      <template v-else-if="material.filename">
        <span class="text-gray-700 font-medium">{{ material.filename }}</span> -
        {{ material.source }}
      </template>
      <template v-else>
        {{ material.source }}
      </template>
    </div>

    <p
      class="text-gray-700 text-sm mb-3 line-clamp-2 flex-grow katex-content"
      v-html="renderedDescription"
    ></p>

    <!-- This section will stick to the bottom -->
    <div class="mt-auto">
      <div
        class="text-sm text-gray-700 font-semibold mb-4 bg-blue-50 px-3 py-2 rounded-lg border-2 border-blue-200 shadow-sm katex-content"
      >
        <span class="text-blue-800">📋</span>
        <span v-html="renderedKeyPoints"></span>
      </div>

      <div class="flex space-x-3">
        <button
          @click="$emit('view', material)"
          class="flex-1 px-4 py-2 bg-white text-blue-800 border-2 border-blue-800 shadow-[4px_4px_0_0_rgba(30,58,138,1)] text-sm font-medium rounded-lg hover:bg-blue-500 hover:text-white transition-colors cursor-pointer"
        >
          <EyeIcon class="w-4 h-4 inline mr-2" />
          View
        </button>
        <button
          @click="handleExportClick"
          class="flex-1 px-4 py-2 bg-white text-green-800 border-2 border-green-800 shadow-[4px_4px_0_0_rgba(21,128,61,1)] text-sm font-medium rounded-lg hover:bg-green-500 hover:text-white transition-colors cursor-pointer"
        >
          <ArrowDownTrayIcon class="w-4 h-4 inline mr-2" />
          Export
        </button>
      </div>
    </div>
  </div>

  <!-- Export Modal -->
  <ExportModal
    :is-open="isExportModalOpen"
    :material="material"
    @close="closeExportModal"
    @export-original="handleExportOriginal"
    @export-a-i="handleExportAI"
  />
</template>

<script setup lang="ts">
import {
  DocumentTextIcon,
  CalendarIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ArrowDownCircleIcon,
} from "@heroicons/vue/24/outline";
import { computed, ref } from "vue";
import { logDebug, logInfo, logError, logSuccess } from "@/utils/secureLogger";
import Swal from "sweetalert2";
import ExportModal from "./ExportModal.vue";
import { renderLatex } from "@/utils/katexRenderer";

interface AIStudyMaterial {
  id: string;
  title: string;
  type: "Summary" | "Flashcards" | "Quiz";
  subject: string;
  source: string;
  filename?: string; // Topic field from Firebase
  date: Date | string; // Allow both Date object and string
  description: string;
  keyPoints: string;
  keyPointsList?: string[];
  student_notes_url?: string; // Firebase Storage URL for original student notes/file
}

interface Props {
  material: AIStudyMaterial;
}

const props = defineProps<Props>();

// Export modal state
const isExportModalOpen = ref(false);

// Format date to the required format: Sept. 2, 2025 - 2:10PM
const formattedDate = computed(() => {
  const date =
    props.material.date instanceof Date ? props.material.date : new Date(props.material.date);

  const monthNames = [
    "Jan.",
    "Feb.",
    "Mar.",
    "Apr.",
    "May",
    "Jun.",
    "Jul.",
    "Aug.",
    "Sept.",
    "Oct.",
    "Nov.",
    "Dec.",
  ];

  const month = monthNames[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'

  return `${month} ${day}, ${year} - ${hours}:${minutes}${ampm}`;
});

// Computed properties for LaTeX rendering
const renderedDescription = computed(() => {
  if (!props.material.description) return "";
  return renderLatex(props.material.description);
});

const renderedKeyPoints = computed(() => {
  if (!props.material.keyPoints) return "";
  return renderLatex(props.material.keyPoints);
});

const getTypeClasses = (type: string) => {
  switch (type) {
    case "Summary":
      return "border border-purple-800 bg-purple-100 text-purple-800";
    case "Flashcards":
      return "border border-green-800 bg-green-100 text-green-800";
    case "Quiz":
      return "border border-orange-800 bg-orange-100 text-orange-800";
    default:
      return "border border-gray-800 bg-gray-100 text-gray-800";
  }
};

/**
 * Download original student notes from Firebase Storage
 */
const downloadStudentNotes = async () => {
  if (!props.material.student_notes_url) {
    logError("No student notes URL available for download");
    await Swal.fire({
      icon: "error",
      title: "Download Failed",
      text: "Original notes are not available for download.",
      confirmButtonColor: "#ef4444",
    });
    return;
  }

  try {
    logInfo("Starting student notes download", {
      filename: props.material.filename,
      materialId: props.material.id,
    });

    // Create a temporary anchor element and trigger download
    // This avoids CORS issues by letting the browser handle the download
    const link = document.createElement("a");
    link.href = props.material.student_notes_url;
    link.download = props.material.filename || "student_notes";
    link.target = "_blank"; // Open in new tab as fallback
    link.rel = "noopener noreferrer";

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);

    logSuccess("Student notes download initiated successfully", {
      filename: props.material.filename,
    });

    await Swal.fire({
      icon: "success",
      title: "Download Started",
      text: `Downloading ${props.material.filename}...`,
      confirmButtonColor: "#10b981",
      timer: 2000,
      timerProgressBar: true,
      showConfirmButton: false,
    });
  } catch (error) {
    logError("Error downloading student notes", error);

    await Swal.fire({
      icon: "error",
      title: "Download Failed",
      text: `Failed to download file: ${error instanceof Error ? error.message : "Unknown error"}`,
      confirmButtonColor: "#ef4444",
    });
  }
};

/**
 * Open export modal when export button is clicked
 */
const handleExportClick = () => {
  logDebug("Export button clicked, opening modal", {
    materialId: props.material.id,
    hasOriginalNotes: !!props.material.student_notes_url,
  });
  isExportModalOpen.value = true;
};

/**
 * Close export modal
 */
const closeExportModal = () => {
  logDebug("Export modal closed");
  isExportModalOpen.value = false;
};

/**
 * Handle export of original student notes
 */
const handleExportOriginal = async (material: AIStudyMaterial) => {
  logInfo("Exporting original student notes", {
    materialId: material.id,
    filename: material.filename,
  });
  await downloadStudentNotes();
};

/**
 * Handle export of AI-generated content
 */
const handleExportAI = (material: AIStudyMaterial) => {
  logInfo("Exporting AI-generated content", {
    materialId: material.id,
    type: material.type,
  });
  // Emit the original export event for AI-generated content
  emit("export", material);
};

const emit = defineEmits<{
  view: [material: AIStudyMaterial];
  export: [material: AIStudyMaterial];
}>();
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Pulse animation for download icon */
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* KaTeX Formula Styling */
.katex-content {
  line-height: 1.8;
}

.katex-content .katex {
  font-size: 1.05em;
}

.katex-content .katex-display {
  margin: 0.5em 0;
  overflow-x: auto;
  overflow-y: hidden;
}

.katex-content .katex-display > .katex {
  text-align: center;
}
</style>
